export const userDocumentation = `
## Fields Specification

| Field | Required | Editable | Type | Formatting | Max Length | Description |
|-------|----------|----------|------|------------|------------|-------------|
| firstName | <span class="checkmark">✓</span> | <span class="checkmark">✓</span> | String | - | - | User's first name |
| lastName | <span class="checkmark">✓</span> | <span class="checkmark">✓</span> | String | - | - | User's last name |
| email | <span class="checkmark">✓</span> | <span class="checkmark">✓</span> | String | Email format | - | User's email address (used for login) |
| role | <span class="checkmark">✓</span> | <span class="checkmark">✓</span> | Enum | provider, assistant | - | User role type |
| <span class="nested-field">providerTitle</span> | <span class="checkmark">✓*</span> | <span class="checkmark">✓</span> | String | - | - | Provider title (e.g., MD, DO, NP) - Required if role is 'provider' |
| <span class="nested-field">providerNPINumber</span> | <span class="checkmark">✓*</span> | <span class="checkmark">✓</span> | String | 10-digit number | 10 | National Provider Identifier - Required if role is 'provider' |
| **userPermissions.managesUsers** | <span class="crossmark">✗</span> | <span class="checkmark">✓</span> | Boolean | - | - | Can create, edit, and remove users in the account |
| **userPermissions.administerClaims** | <span class="crossmark">✗</span> | <span class="checkmark">✓</span> | Boolean | - | - | Manage claims, claim reportables, and claim reports |
| **userPermissions.receiveClaimsEmails** | <span class="crossmark">✗</span> | <span class="checkmark">✓</span> | Boolean | - | - | Will receive periodic emails with claim statistics |
| **userPermissions.receiveSurveysEmails** | <span class="crossmark">✗</span> | <span class="checkmark">✓</span> | Boolean | - | - | Will receive periodic emails with assessment statistics |
| **userPermissions.administerClaimReports** | <span class="crossmark">✗</span> | <span class="checkmark">✓</span> | Boolean | - | - | Administer claim reports |
| **userPermissions.imminentRiskEmails** | <span class="crossmark">✗</span> | <span class="checkmark">✓</span> | Boolean | - | - | Will receive email notification for imminent risk patients |
| **userPermissions.managesAlert** | <span class="crossmark">✗</span> | <span class="checkmark">✓</span> | Boolean | - | - | Alerts will be considered 'read' if viewed by user; can take action on alerts |
| allowedAccounts | <span class="crossmark">✗</span> | <span class="checkmark">✓</span> | Array | Account UUIDs | - | List of specific accounts user has access to (empty = all accounts) |

*Required only when role is 'provider'

## Validation Rules

1. **Basic Information**
   - firstName is required field
   - lastName is required field
   - email is required and must be valid email format
   - role is required and must be either 'provider' or 'assistant'

2. **Provider-Specific Fields**
   - When role is 'provider':
     - providerTitle is required (placeholder: "e.g., MD, DO, NP")
     - providerNPINumber is required (placeholder: "10-digit NPI number")
   - When role is 'assistant':
     - Provider fields are hidden and not required

3. **Permission Dependencies**
   - receiveClaimsEmails is only enabled if administerClaims is true
   - receiveClaimsEmails is not shown for Distribution Sponsor (DS) account users
   - receiveSurveysEmails is not shown for Distribution Sponsor (DS) account users

4. **Account Access Rules**
   - allowedAccounts is only visible for Distribution Sponsor account users
   - Empty allowedAccounts array = unlimited access to all accounts
   - Non-empty array = limited access to specified accounts only

## Permissions

### User Creation
Required permission: \`ACCOUNT_USER_CREATE\`

### User Reading
Required permission: \`ACCOUNT_USER_GET\`

### User Updating
Required permission: \`ACCOUNT_USER_PATCH\`

### User Deletion
Required permission: \`ACCOUNT_USER_DELETE\`

### User Listing
Required permission: \`ACCOUNT_USERS_GET\`

### Distribution Sponsor Features
Required permission: \`ACCOUNTS_GET\` - Enables multi-account user management

## API Endpoints

| Method | Endpoint | Description |
|--------|----------|-------------|
| PUT | \`/accounts/{accountUuid}/users\` | Create new user |
| GET | \`/accounts/{accountUuid}/users/{userUuid}\` | Get user details |
| PATCH | \`/accounts/{accountUuid}/users/{userUuid}\` | Update user |
| DELETE | \`/accounts/{accountUuid}/users/{userUuid}\` | Delete user |
| GET | \`/accounts/{accountUuid}/users\` | List users |
| POST | \`/accounts/{accountUuid}/users/{userUuid}/invitation\` | Resend invitation |

## Request/Response Examples

### Create User (PUT)
**Request Body:**
\`\`\`json
{
  "firstName": "John",
  "lastName": "Doe",
  "email": "john.doe@example.com",
  "role": "provider",
  "providerTitle": "MD",
  "providerNPINumber": "1234567890",
  "allowedAccounts": [],
  "userPermissions": {
    "managesUsers": false,
    "administerClaims": true,
    "receiveClaimsEmails": false,
    "receiveSurveysEmails": false,
    "administerClaimReports": false,
    "imminentRiskEmails": true,
    "managesAlert": false
  }
}
\`\`\`

**Success Response (201 Created):**
\`\`\`json
{
  "uuid": "user-uuid-here",
  "firstName": "John",
  "lastName": "Doe",
  "email": "john.doe@example.com",
  "role": "provider",
  "providerTitle": "MD",
  "providerNPINumber": "1234567890",
  "allowedAccounts": [],
  "userPermissions": {
    "managesUsers": false,
    "administerClaims": true,
    "receiveClaimsEmails": false,
    "receiveSurveysEmails": false,
    "administerClaimReports": false,
    "imminentRiskEmails": true,
    "managesAlert": false
  },
  "canResendInvitation": true
}
\`\`\`

### Update User (PATCH)
**Request Body (JSON Patch format):**
\`\`\`json
[
  {
    "op": "replace",
    "path": "/userPermissions/administerClaims",
    "value": true
  },
  {
    "op": "replace",
    "path": "/providerTitle",
    "value": "DO"
  }
]
\`\`\`

**Success Response (200 OK):**
\`\`\`json
{
  "uuid": "user-uuid-here",
  "firstName": "John",
  "lastName": "Doe",
  "email": "john.doe@example.com",
  "role": "provider",
  "providerTitle": "DO",
  "providerNPINumber": "1234567890",
  "allowedAccounts": [],
  "userPermissions": {
    "managesUsers": false,
    "administerClaims": true,
    "receiveClaimsEmails": false,
    "receiveSurveysEmails": false,
    "administerClaimReports": false,
    "imminentRiskEmails": true,
    "managesAlert": false
  },
  "canResendInvitation": true
}
\`\`\`

## HTTP Status Codes

| Status Code | Scenario | Description |
|-------------|----------|-------------|
| 200 OK | Successful GET/PATCH | User retrieved/updated successfully |
| 201 Created | Successful PUT | User created successfully |
| 204 No Content | Successful DELETE | User deleted successfully |
| 422 Unprocessable Entity | Validation errors | Invalid request body or validation failures |
| 500 Internal Server Error | Business rule violation | User cannot be removed or too many invitations sent |

## Error Response Format

**Validation Errors (422 Unprocessable Entity):**
\`\`\`json
{
  "correlationUUID": "correlation-id",
  "validationErrors": {
    "firstName": "First name is required",
    "email": "Invalid email format",
    "providerNPINumber": "NPI number must be 10 digits"
  }
}
\`\`\`

**Business Rule Errors (500 Internal Server Error):**
\`\`\`json
{
  "errorCode": 1,
  "message": "User cannot be removed",
  "correlationUUID": "correlation-id"
}
\`\`\`

## Route Structure

### Standard Account Routes
- List: \`/{currentAccountUuid}/users\`
- Create: \`/{currentAccountUuid}/users/create\`  
- Edit: \`/{currentAccountUuid}/users/{userUuid}/edit\`

### Distribution Sponsor Routes
- List: \`/{currentAccountUuid}/accounts/{accountUuid}/users\`
- Create: \`/{currentAccountUuid}/accounts/{accountUuid}/users/create\`
- Edit: \`/{currentAccountUuid}/accounts/{accountUuid}/users/{userUuid}/edit\`

## Business Rules

1. **Role-Based Requirements**
   - Provider role requires additional medical credentials (title, NPI number)
   - Assistant role has basic administrative access

2. **User States**
   - New User: Invitation sent automatically after creation
   - Existing User: Can resend invitations if needed
   - Some users cannot be deleted due to business constraints

3. **Distribution Sponsor Features**
   - Users with ACCOUNTS_GET permission can manage users across multiple accounts
   - Account access restrictions can be set for users
   - Empty allowedAccounts means unlimited access

4. **Permission Dependencies**
   - Claims email notifications require administer claims permission
   - Email permissions are not shown for DS account users
   - Imminent risk emails are independent of other permissions
`;